import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Trash2, AlertTriangle, Search, RefreshCw, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CustomCityManager = () => {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.superAdmin;
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [cities, setCities] = useState([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/custom-cities`, auth);
      setCities(res.data.cities || []);
      setInvalidCount(res.data.invalidCount || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load custom cities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteOne = async (city) => {
    if (!window.confirm(`Delete "${city.city}" from custom cities?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/custom-cities/${city.id}`, auth);
      toast.success(`Deleted "${city.city}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const cleanupInvalid = async () => {
    if (!isSuperAdmin) return;
    if (!invalidCount) {
      toast.info('Nothing to clean up — no invalid entries');
      return;
    }
    if (!window.confirm(`This will permanently delete ${invalidCount} invalid entries. Continue?`)) return;
    setCleaning(true);
    try {
      const res = await axios.delete(`${API_URL}/api/admin/custom-cities/invalid`, auth);
      toast.success(`Cleaned up ${res.data.deleted} invalid entries`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const filtered = cities.filter((c) => {
    if (showOnlyInvalid && c.isValid) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.city || '').toLowerCase().includes(q)
      || (c.state || '').toLowerCase().includes(q)
      || (c.country || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
          <MapPin className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Custom Cities</h1>
          <p className="text-sm text-gray-500">Cities added by users through registration forms</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs uppercase text-gray-500">Total</div>
          <div className="text-2xl font-bold text-gray-900" data-testid="custom-cities-total">{cities.length}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs uppercase text-amber-700">Invalid</div>
          <div className="text-2xl font-bold text-amber-800" data-testid="custom-cities-invalid">{invalidCount}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs uppercase text-emerald-700">Valid</div>
          <div className="text-2xl font-bold text-emerald-800">{cities.length - invalidCount}</div>
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search city, state or country"
              className="pl-9"
              data-testid="custom-cities-search"
            />
          </div>
          <label className="text-sm flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyInvalid}
              onChange={(e) => setShowOnlyInvalid(e.target.checked)}
              className="w-4 h-4"
              data-testid="custom-cities-only-invalid"
            />
            Show only invalid
          </label>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Refresh</>}
          </Button>
          <Button
            onClick={cleanupInvalid}
            disabled={cleaning || !isSuperAdmin || invalidCount === 0}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            title={isSuperAdmin ? '' : 'Super admin only'}
            data-testid="custom-cities-cleanup"
          >
            {cleaning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
            Clean Up Invalid ({invalidCount})
          </Button>
        </div>
        {!isSuperAdmin && (
          <p className="text-xs text-gray-500 mt-2">Only the super admin can bulk-clean invalid entries. Individual deletes are allowed.</p>
        )}
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No cities match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 border-b bg-gray-50">
                <tr>
                  <th className="py-2 px-3">City</th>
                  <th className="py-2 px-3">State</th>
                  <th className="py-2 px-3">Country</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Added</th>
                  <th className="py-2 px-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className={`border-b last:border-0 hover:bg-gray-50 ${!c.isValid ? 'bg-amber-50/40' : ''}`}>
                    <td className="py-2 px-3 font-medium text-gray-900">{c.city}</td>
                    <td className="py-2 px-3 text-gray-700">{c.state || '—'}</td>
                    <td className="py-2 px-3 text-gray-700">{c.country || '—'}</td>
                    <td className="py-2 px-3">
                      {c.isValid ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" /> Valid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          <XCircle className="w-3 h-3" /> Invalid
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-500">
                      {c.addedDate ? new Date(c.addedDate).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => deleteOne(c)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CustomCityManager;
